import { useRef, useCallback, useState, useEffect } from 'react';
import { Howl } from 'howler';

export const AUDIO_PLAYBACK_RATES = [1, 1.25, 1.5, 2] as const;
export type AudioPlaybackRate = (typeof AUDIO_PLAYBACK_RATES)[number];

interface UseBotAudioOptions {
  isEnabled: boolean;
}

interface UseBotAudioReturn {
  play: (base64Audio: string) => void;
  stop: () => void;
  pause: () => void;
  resume: () => void;
  togglePause: () => void;
  cyclePlaybackRate: () => void;
  setPlaybackRate: (rate: AudioPlaybackRate) => void;
  resetPlaybackRate: () => void;
  isSpeaking: boolean;
  isPaused: boolean;
  isPlayerVisible: boolean;
  elapsedSeconds: number;
  playbackRate: AudioPlaybackRate;
  hasPendingAudio: boolean;
}

/**
 * Hook TTS - Bot audio playback
 * Sử dụng Howler.js để phát audio base64 dạng stream chunks từ backend nối đuôi nhau.
 */
export function useBotAudio(options: UseBotAudioOptions): UseBotAudioReturn {
  const { isEnabled } = options;

  const soundRef = useRef<Howl | null>(null);
  const isEnabledRef = useRef<boolean>(isEnabled);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [isPlayerVisible, setIsPlayerVisible] = useState(false);
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const [playbackRate, setPlaybackRateState] = useState<AudioPlaybackRate>(1);

  const audioQueueRef = useRef<string[]>([]);
  const prevEnabledRef = useRef<boolean>(isEnabled);
  const isPlayingRef = useRef<boolean>(false);
  const isPausedRef = useRef<boolean>(false);
  const playbackRateRef = useRef<AudioPlaybackRate>(1);
  const baseElapsedRef = useRef(0);
  const elapsedTickerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const clearElapsedTicker = useCallback(() => {
    if (elapsedTickerRef.current) {
      clearInterval(elapsedTickerRef.current);
      elapsedTickerRef.current = null;
    }
  }, []);

  const syncElapsed = useCallback(() => {
    const sound = soundRef.current;
    const seek = sound ? Number(sound.seek()) || 0 : 0;
    setElapsedSeconds(Math.floor(baseElapsedRef.current + seek));
  }, []);

  const startElapsedTicker = useCallback(() => {
    clearElapsedTicker();
    syncElapsed();
    elapsedTickerRef.current = setInterval(syncElapsed, 250);
  }, [clearElapsedTicker, syncElapsed]);

  const resetPlaybackRate = useCallback(() => {
    playbackRateRef.current = 1;
    setPlaybackRateState(1);
    if (soundRef.current) {
      soundRef.current.rate(1);
    }
  }, []);

  const resetSession = useCallback(() => {
    clearElapsedTicker();
    baseElapsedRef.current = 0;
    setElapsedSeconds(0);
    setIsPaused(false);
    isPausedRef.current = false;
    setIsPlayerVisible(false);
    setIsSpeaking(false);
    isPlayingRef.current = false;
    resetPlaybackRate();
  }, [clearElapsedTicker, resetPlaybackRate]);

  useEffect(() => {
    return () => {
      clearElapsedTicker();
      if (soundRef.current) {
        soundRef.current.stop();
        soundRef.current.unload();
        soundRef.current = null;
      }
    };
  }, [clearElapsedTicker]);

  useEffect(() => {
    const wasPreviouslyEnabled = prevEnabledRef.current;
    prevEnabledRef.current = isEnabled;
    isEnabledRef.current = isEnabled;

    if (wasPreviouslyEnabled && !isEnabled) {
      if (soundRef.current) {
        soundRef.current.volume(0);
      }
      if (!isPausedRef.current) {
        setIsSpeaking(false);
      }
    } else if (!wasPreviouslyEnabled && isEnabled) {
      if (soundRef.current) {
        soundRef.current.volume(1.0);
      }
      if (isPlayingRef.current && !isPausedRef.current) {
        setIsSpeaking(true);
      }
    }
  }, [isEnabled]);

  const playNext = useCallback(() => {
    if (isPausedRef.current) {
      return;
    }

    if (audioQueueRef.current.length === 0) {
      clearElapsedTicker();
      syncElapsed();
      isPlayingRef.current = false;
      setIsSpeaking(false);
      setIsPlayerVisible(false);
      setIsPaused(false);
      isPausedRef.current = false;
      soundRef.current = null;
      resetPlaybackRate();
      return;
    }

    const nextChunk = audioQueueRef.current[0];
    if (!nextChunk) {
      audioQueueRef.current.shift();
      playNext();
      return;
    }

    const sound = new Howl({
      src: [`data:audio/mpeg;base64,${nextChunk}`],
      format: ['mp3'],
      html5: false,
      volume: isEnabledRef.current ? 1.0 : 0,
      rate: playbackRateRef.current,
      preload: true,
      onplay: () => {
        isPlayingRef.current = true;
        isPausedRef.current = false;
        setIsPaused(false);
        setIsPlayerVisible(true);
        if (isEnabledRef.current) {
          setIsSpeaking(true);
        }
        startElapsedTicker();
      },
      onpause: () => {
        clearElapsedTicker();
        syncElapsed();
        setIsSpeaking(false);
        setIsPaused(true);
        isPausedRef.current = true;
      },
      onend: () => {
        const finished = soundRef.current;
        const chunkDuration = finished ? finished.duration() || 0 : 0;
        baseElapsedRef.current += chunkDuration;
        audioQueueRef.current.shift();
        playNext();
      },
      onstop: () => {
        // stop() clears session elsewhere; ignore pause-related stops
      },
      onloaderror: (_id, error) => {
        console.error('[TTS] Load error on chunk:', error);
        audioQueueRef.current.shift();
        playNext();
      },
      onplayerror: (_id, error) => {
        console.error('[TTS] Play error on chunk, unlocking and retrying...', error);
        sound.once('unlock', () => {
          sound.play();
        });
      },
    });

    soundRef.current = sound;
    sound.play();
  }, [clearElapsedTicker, resetPlaybackRate, startElapsedTicker, syncElapsed]);

  const play = useCallback((base64AudioChunk: string) => {
    if (!base64AudioChunk) return;

    if (!isPlayingRef.current && audioQueueRef.current.length === 0) {
      baseElapsedRef.current = 0;
      setElapsedSeconds(0);
      resetPlaybackRate();
    }

    audioQueueRef.current.push(base64AudioChunk);
    setIsPlayerVisible(true);

    if (!isPlayingRef.current && !isPausedRef.current) {
      isPlayingRef.current = true;
      playNext();
    }
  }, [playNext, resetPlaybackRate]);

  const stop = useCallback(() => {
    if (soundRef.current) {
      soundRef.current.stop();
      soundRef.current.unload();
      soundRef.current = null;
    }
    audioQueueRef.current = [];
    resetSession();
  }, [resetSession]);

  const pause = useCallback(() => {
    if (!soundRef.current || isPausedRef.current) return;
    soundRef.current.pause();
  }, []);

  const resume = useCallback(() => {
    if (!isPausedRef.current) return;
    isPausedRef.current = false;
    setIsPaused(false);
    if (soundRef.current) {
      soundRef.current.play();
      return;
    }
    if (audioQueueRef.current.length > 0) {
      isPlayingRef.current = true;
      playNext();
    }
  }, [playNext]);

  const togglePause = useCallback(() => {
    if (isPausedRef.current) {
      resume();
    } else if (isPlayingRef.current || soundRef.current) {
      pause();
    }
  }, [pause, resume]);

  const setPlaybackRate = useCallback((rate: AudioPlaybackRate) => {
    playbackRateRef.current = rate;
    setPlaybackRateState(rate);
    if (soundRef.current) {
      soundRef.current.rate(rate);
    }
  }, []);

  const cyclePlaybackRate = useCallback(() => {
    const idx = AUDIO_PLAYBACK_RATES.indexOf(playbackRateRef.current);
    const next = AUDIO_PLAYBACK_RATES[(idx + 1) % AUDIO_PLAYBACK_RATES.length];
    setPlaybackRate(next);
  }, [setPlaybackRate]);

  return {
    play,
    stop,
    pause,
    resume,
    togglePause,
    cyclePlaybackRate,
    setPlaybackRate,
    resetPlaybackRate,
    isSpeaking,
    isPaused,
    isPlayerVisible,
    elapsedSeconds,
    playbackRate,
    hasPendingAudio: false,
  };
}

export default useBotAudio;
